import { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Paper, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Rating, IconButton, Avatar, Card, CardContent, Fade, Skeleton
} from '@mui/material';
import {
  Launch as LaunchIcon,
  LocalOffer as OfferIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Visibility as VisibilityIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import CustomLinearProgress from '../Loaders/CustomLinearProgress';
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import { useAuth } from '../../contexts/AuthContext';
import VariantsModal from '../../components/Modals/VariantsModal';

const SearchedProductsTable = ({ products, setCurrProduct, isLoading, handleNext, }) => {
  const { theme } = useTheme();
  const { isSubUser } = useAuth();
  const [selectedValue, setSelectedValue] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  const columnConfig = useMemo(() => {
    if (!products || products.length === 0) return {};

    return {
      hasSource: products.some(item => item.source),
      hasCost: products.some(item => item.cost),
      hasSellingPrice: products.some(item => item.sellingPrice),
      hasBarcode: products.some(item => item.Barcode),
      hasNumReviews: products.some(item => item.num_reviews),
      hasCustomerRating: products.some(item => item.customerRating),
      hasOfferCount: products.some(item => item.offerCount),
      hasBadges: products.some(item => item.badges && item.badges.length > 0),
      hasPrice: products.some(item => item.price)
    };
  }, [products]);

  const styles = {
    container: {
      maxHeight: isSubUser ? 'calc(65vh - 40px)' : '65vh',
      overflowY: 'auto',
      backgroundColor: theme.background.secondary,
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
        borderRadius: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar,
        borderRadius: '8px',
        cursor: 'grab',
        '&:hover': {
          backgroundColor: theme.scrollbarHover,
        }
      },
    },
    tableRow: {
      transition: 'all 0.2s ease-in-out',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.background.primary,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      },
      '&.selected': {
        backgroundColor: `${theme.color.primary}15`,
        borderLeft: `4px solid ${theme.color.primary}`,
      }
    },
    th: {
      fontWeight: "bold",
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 18,
      position: "sticky",
      top: 0,
      backgroundColor: theme.background.secondary,
      zIndex: 1,
    },
    td: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 16,
      cursor: 'pointer'
    },
    productImage: {
      borderRadius: "12px",
      border: `2px solid ${theme.border.light}`,
      height: "80px",
      width: "80px",
      objectFit: "cover",
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
        borderColor: theme.color.primary,
      }
    },
    selectButton: {
      borderRadius: '12px',
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      fontWeight: '500',
      padding: '10px 20px',
      background: `linear-gradient(135deg, ${theme.color.primary} 0%, ${theme.color.primary}CC 100%)`,
      color: 'white',
      border: 'none',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        background: `linear-gradient(135deg, ${theme.color.primary}DD 0%, ${theme.color.primary}AA 100%)`,
      }
    },
    priceChip: {
      fontWeight: '600',
      borderRadius: '8px',
      fontSize: '14px',
      fontFamily: theme.fontFamily
    },
    validPrice: {
      backgroundColor: `${theme.color.success || '#4caf50'}20`,
      color: theme.color.success || '#4caf50',
    },
    invalidPrice: {
      backgroundColor: `${theme.accent.error}20`,
      color: theme.accent.error,
    },
    itemIdChip: {
      backgroundColor: `${theme.color.primary}15`,
      color: theme.color.primary,
      fontWeight: '500',
      fontSize: '12px',
      fontFamily: theme.fontFamily
    }
  };

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
    const selectedItem = products.find(obj => obj.itemId === event.target.value);
    setCurrProduct(selectedItem);
  };

  const handleChoose = (product) => {
    setTimeout(async () => {
      setCurrProduct(product);
      await handleNext(product);
    }, 0);
  };

  const handleRowClick = (product) => {
    setSelectedValue(product.itemId);
    setCurrProduct(product);
  };

  const handleImageError = (productId) => {
    setImageLoadErrors(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return null;
    const amount = typeof price === 'object' ? price.amount : price;
    return amount ? `$${parseFloat(amount).toFixed(2)}` : null;
  };

  const renderPriceChip = (price, label) => {
    const formattedPrice = formatPrice(price);
    const isValid = formattedPrice !== null;

    return (
      <Chip
        label={formattedPrice || 'Invalid price'}
        icon={!isValid && <ErrorIcon sx={{ fontSize: 16 }} />}
        sx={{
          ...styles.priceChip,
          ...(isValid ? styles.validPrice : styles.invalidPrice),
        }}
        size="small"
      />
    );
  };

  const renderProductImage = (product) => {
    const imageId = product.dbId || product.itemId;

    if (imageLoadErrors[imageId]) {
      return (
        <Avatar
          sx={{
            width: 80,
            height: 80,
            backgroundColor: theme.background.primary,
            border: `2px solid ${theme.border.light}`,
          }}
        >
          <VisibilityIcon sx={{ color: theme.text.secondary, fontSize: 32 }} />
        </Avatar>
      );
    }

    return (
      <Box
        component="img"
        src={product.image || "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg"}
        onError={() => handleImageError(imageId)}
        sx={styles.productImage}
        onClick={() => handleChoose(product)}
        alt={product.title}
      />
    );
  };

  const renderAdditionalInfo = (product) => {
    const infoItems = [];

    if (columnConfig.hasCustomerRating && product.customerRating && parseFloat(product.customerRating) > 0) {
      infoItems.push(
        <Box key="rating" display="flex" alignItems="center" gap={1}>
          <Rating
            value={parseFloat(product.customerRating)}
            readOnly
            size="small"
            precision={0.1}
          />
          <Typography sx={{ fontSize: '12px', color: theme.text.secondary, fontFamily: theme.fontFamily }}>
            ({product.customerRating})
          </Typography>
        </Box>
      );
    }

    if (columnConfig.hasNumReviews && product.num_reviews && parseInt(product.num_reviews) > 0) {
      infoItems.push(
        <Chip
          key="reviews"
          label={`${product.num_reviews} reviews`}
          size="small"
          sx={{ fontSize: '11px', fontFamily: theme.fontFamily, backgroundColor: theme.background.primary, color: theme.text.primary }}
        />
      );
    }

    if (columnConfig.hasOfferCount && product.offerCount && parseInt(product.offerCount) > 0) {
      infoItems.push(
        <Chip
          key="offers"
          icon={<OfferIcon sx={{ fontSize: 14 }} />}
          label={`${product.offerCount} offers`}
          size="small"
          sx={{ fontSize: '11px', fontFamily: theme.fontFamily, backgroundColor: theme.background.primary, color: theme.text.primary }}
        />
      );
    }

    if (columnConfig.hasBadges && product.badges && product.badges.length > 0) {
      infoItems.push(
        <HtmlTooltip
          key="badges"
          customtheme={theme}
          title={
            <Box>
              {product.badges.map((badge, badgeIndex) => (
                <Chip
                  key={badgeIndex}
                  label={badge}
                  size="small"
                  sx={{
                    m: 0.25,
                    fontSize: '10px',
                    backgroundColor: theme.background.secondary,
                    color: theme.text.primary,
                    fontFamily: theme.fontFamily
                  }}
                />
              ))}
            </Box>
          }
          arrow
          placement="right"
        >
          <Chip
            label="Badges"
            size="small"
            sx={{
              fontSize: '11px',
              fontFamily: theme.fontFamily,
              backgroundColor: theme.background.primary,
              color: theme.text.primary,
              cursor: 'pointer'
            }}
          />
        </HtmlTooltip>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {infoItems}
      </Box>
    );
  };

  useEffect(() => {
    if (products.length > 0) {
      setSelectedValue(products[0]?.itemId);
    }
  }, [products]);

  if (isLoading) {
    return (
      <Box m={2}>
        <Card sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <CardContent>
            <CustomLinearProgress />
            <Box mt={2}>
              {[...Array(3)].map((_, index) => (
                <Box key={index} display="flex" alignItems="center" mb={2}>
                  <Skeleton variant="circular" width={80} height={80} sx={{ mr: 2 }} />
                  <Box flex={1}>
                    <Skeleton variant="text" width="70%" height={24} />
                    <Skeleton variant="text" width="50%" height={20} />
                    <Skeleton variant="text" width="30%" height={20} />
                  </Box>
                  <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '8px' }} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const getColumnWidths = () => {
    const baseColumns = 4;
    let additionalColumns = 0;

    if (columnConfig.hasCost) additionalColumns++;
    if (columnConfig.hasSellingPrice) additionalColumns++;
    if (columnConfig.hasSource) additionalColumns++;
    if (columnConfig.hasNumReviews || columnConfig.hasCustomerRating || columnConfig.hasOfferCount || columnConfig.hasBadges) additionalColumns++;

    return {
      select: '60px',
      product: `${Math.max(35, 50 - additionalColumns * 3)}%`,
      info: columnConfig.hasNumReviews || columnConfig.hasCustomerRating || columnConfig.hasOfferCount || columnConfig.hasBadges ? '15%' : '0%',
      cost: columnConfig.hasCost ? '12%' : '0%',
      selling: columnConfig.hasSellingPrice ? '12%' : '0%',
      price: columnConfig.hasPrice ? '12%' : '0%',
      source: columnConfig.hasSource ? '10%' : '0%',
      action: '120px'
    };
  };

  const colWidths = getColumnWidths();

  return (
    <Box m={2}>
      <TableContainer component={Paper} sx={styles.container}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: colWidths.select, ...styles.th }}>Select</TableCell>
              <TableCell sx={{ width: colWidths.product, ...styles.th }}>Product Details</TableCell>
              {(columnConfig.hasNumReviews || columnConfig.hasCustomerRating || columnConfig.hasOfferCount || columnConfig.hasBadges) && (
                <TableCell sx={{ width: colWidths.info, ...styles.th }}>Additional Info</TableCell>
              )}
              {columnConfig.hasCost && (
                <TableCell sx={{ width: colWidths.cost, ...styles.th }}>Cost Price</TableCell>
              )}
              {columnConfig.hasSellingPrice && (
                <TableCell sx={{ width: colWidths.selling, ...styles.th }}>Sell Price</TableCell>
              )}
              {columnConfig.hasPrice && (
                <TableCell sx={{ width: colWidths.price, ...styles.th }}>WM Price</TableCell>
              )}
              {columnConfig.hasSource && (
                <TableCell sx={{ width: colWidths.source, ...styles.th }}>Source</TableCell>
              )}
              <TableCell sx={{ width: colWidths.action, ...styles.th }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={Object.values(columnConfig).filter(Boolean).length + 3}>
                  <CustomLinearProgress />
                </TableCell>
              </TableRow>
            )}
            {products.map((product, index) => (
              <Fade in={true} timeout={300 + index * 100} key={product.itemId || index}>
                <TableRow
                  className={selectedValue === product.itemId ? 'selected' : ''}
                  sx={styles.tableRow}
                  onClick={() => handleRowClick(product)}
                >
                  <TableCell sx={styles.td}>
                    <Radio
                      checked={selectedValue === product.itemId}
                      onChange={handleChange}
                      value={product.itemId}
                      name="radio-buttons"
                      sx={{
                        color: theme.text.tertiary,
                        "&.Mui-checked": {
                          color: theme.color.primary,
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={styles.td}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {renderProductImage(product)}
                      <Box flex={1} minWidth={0}>
                        <Typography
                          sx={{
                            fontWeight: '600',
                            fontSize: '14px',
                            color: theme.text.primary,
                            cursor: 'pointer',
                            mb: 1,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontFamily: theme.fontFamily
                          }}
                        >
                          {product.title || product.productName || product.product_name}
                        </Typography>

                        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                          {product.itemId && (
                            <Chip
                              icon={<InventoryIcon sx={{ fontSize: 14 }} />}
                              label={`Item ID: ${product.itemId}`}
                              size="small"
                              sx={styles.itemIdChip}
                            />
                          )}

                          {product.upc && (
                            <Chip
                              label={`UPC: ${product.upc}`}
                              size="small"
                              sx={{
                                backgroundColor: theme.background.primary,
                                color: theme.text.secondary,
                                fontSize: '11px',
                                fontFamily: theme.fontFamily
                              }}
                            />
                          )}

                          {columnConfig.hasBarcode && product.Barcode && (
                            <Chip
                              label={`Barcode: ${product.Barcode}`}
                              size="small"
                              sx={{
                                backgroundColor: theme.background.primary,
                                color: theme.text.secondary,
                                fontSize: '11px',
                                fontFamily: theme.fontFamily
                              }}
                            />
                          )}
                        </Box>

                        {product.itemId && (
                          <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://www.walmart.com/ip/${product.itemId}`, '_blank');
                            }}
                            sx={{
                              color: theme.color.primary,
                              '&:hover': {
                                backgroundColor: `${theme.color.primary}20`,
                              }
                            }}
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                          <Typography sx={{
                            fontSize: '12px',
                            color: theme.color.primary,
                            fontWeight: '500',
                            fontFamily: theme.fontFamily,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: "underline"
                            }
                          }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://www.walmart.com/ip/${product.itemId}`, '_blank');
                            }}
                          >
                            View on Walmart
                          </Typography>
                        </Box>
                      )}
                      </Box>
                    </Box>
                  </TableCell>

                  {(columnConfig.hasNumReviews || columnConfig.hasCustomerRating || columnConfig.hasOfferCount || columnConfig.hasBadges) && (
                    <TableCell sx={styles.td}>
                      {renderAdditionalInfo(product)}
                    </TableCell>
                  )}

                  {columnConfig.hasCost && (
                    <TableCell sx={styles.td}>
                      {renderPriceChip(product.cost, 'Cost')}
                    </TableCell>
                  )}

                  {columnConfig.hasSellingPrice && (
                    <TableCell sx={styles.td}>
                      {renderPriceChip(product.sellingPrice, 'Selling Price')}
                    </TableCell>
                  )}

                  {columnConfig.hasPrice && (
                    <TableCell sx={styles.td}>
                      {renderPriceChip(product.price, 'WM Price')}
                    </TableCell>
                  )}

                  {columnConfig.hasSource && (
                    <TableCell sx={styles.td}>
                      <Typography sx={{
                        fontFamily: theme.fontFamily,
                        color: theme.text.primary,
                        fontSize: '14px'
                      }}>
                        {product.source}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell sx={styles.td}>
                    <Button
                      variant="contained"
                      sx={styles.selectButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChoose(product);
                      }}
                      size="small"
                    >
                      Select
                    </Button>
                    {product?.variants && product?.variants.length > 1 && <VariantsModal product={product} variants={product.variants} handleSelect={(variant => handleChoose(variant))}/>}
                  </TableCell>
                </TableRow>
              </Fade>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SearchedProductsTable;